import { EventEmitter, Injectable, Output, ViewChild } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { Observable } from 'rxjs/internal/Observable';
import { catchError, filter, take, switchMap, tap, map } from 'rxjs/operators';
import { throwError, BehaviorSubject } from 'rxjs';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { AuthService } from '../auth/auth.service';
import * as urls from './urls';
import { AuthUser, User } from '../models/auth-user';
import { SessionExpiredError } from './errors';
import { Messages } from './message';
import { Config } from 'src/environments/config';
const omitEmpty = require('omit-empty');
import { EncryptionService } from '../encryption/encryption.service';
import { Router } from '@angular/router';
import { AuthRoutes } from './routes';

@Injectable()
export class GlobalHttpInterceptorService implements HttpInterceptor {
  private refreshTokenInProgress = false;

  // Refresh Token Subject tracks the current token, or is null if no token is currently
  // available (e.g. refresh pending).

  private refreshTokenSubject: BehaviorSubject < any > = new BehaviorSubject < any > (
      null
  );

  constructor(
      private notification: NzNotificationService,
      private authService: AuthService,
      private drawerRef: NzDrawerRef,
      private encryption: EncryptionService,
      private modalService: NzModalService,
      private router: Router 
) {}

  intercept(request: HttpRequest < any > , next: HttpHandler): Observable < HttpEvent < any >> {
      // Remove empty fields from request body

      request = request.clone({
          body: omitEmpty(request.body)
      });
      if (this.checkIsEncrption(request.url) == false &&
          request.method !== 'GET' && request.method !== 'DELETE') {
          request = request.clone({
              body: this.encryption.encrypt(request.body)
          });
          const response = {
              requestData: request.body,
          };
          request = request.clone({
              body: response
          });
      }
      return next.handle(this.addAuthenticationToken(request)).pipe(
          map((event: HttpEvent < any > ) => {
              if (event instanceof HttpResponse) {
                  if (event.body && event.body.responseData) {
                      if (this.checkIsEncrption(request.url) == false) {
                          event = event.clone < any > ({
                              body: this.encryption.decrypt(event.body.responseData),
                          });
                      }
                  }
              }
              return event;
          }),
          catchError((error) => {
              // We don't want to refresh token for some requests like login or refresh token itself
              // So we verify url and we throw an error if it's the case
              if (request.url.includes(urls.AuthUrls.refreshCustomToken) || request.url.includes(urls.AuthUrls.login) || request.url.includes(urls.AuthUrls.verifyOTP)) {
                  // We do another check to see if refresh token failed
                  // In this case we want to logout user and to redirect it to login page

                  this.refreshTokenInProgress = false;
                  if (request.url.includes(urls.AuthUrls.refreshCustomToken)) {
                      this.authService.onNotify(true);
                      this.authService.logout();
                      return this.handleError(new SessionExpiredError(), this.notification);
                  }

                  return this.handleError(error, this.notification);
              }

              if (error.status !== 401) {
                  return this.handleError(error, this.notification);
              }

              if (this.refreshTokenInProgress) {
                  // If refreshTokenInProgress is true, we will wait until refreshTokenSubject has a non-null value
                  // – which means the new token is ready and we can retry the request again
                  return this.refreshTokenSubject.pipe(
                      filter((result) => result !== null),
                      take(1),
                      switchMap(() =>
                          next.handle(this.addAuthenticationToken(request)).pipe(
                              map((event: HttpEvent < any > ) => {
                                  if (event instanceof HttpResponse) {
                                    if (event.body && event.body.responseData) {
                                          if (this.checkIsEncrption(request.url) == false) {
                                              event = event.clone < any > ({
                                                  body: this.encryption.decrypt(
                                                      event.body.responseData
                                                  ),
                                              });
                                          }
                                      }
                                  }
                                  return event;
                              })
                          )
                      ),
                      catchError((err: any) => {
                          this.refreshTokenInProgress = false;
                          if (err instanceof HttpErrorResponse && err.error && err.error.statusCode === 400) {
                              return this.handleError(err, this.notification);
                          } else {
                              this.authService.onNotify(true); // For Drawer Close
                              this.authService.logout();
                              return this.handleError(err, this.notification);
                          }
                      })
                  );
              } else {
                  this.refreshTokenInProgress = true;

                  // Set the refreshTokenSubject to null so that subsequent API calls will wait until the new token has been retrieved
                  this.refreshTokenSubject.next(null);

                  // Call auth.refreshAccessToken(this is an Observable that will be returned)
                  // return this.authService
                  return this.authService.refreshCustomTokens().pipe(
                      switchMap((authUser: User) => {
                          // When the call to refreshToken completes we reset the refreshTokenInProgress to false
                          // for the next time the token needs to be refreshed
                          this.refreshTokenInProgress = false;
                          this.refreshTokenSubject.next(authUser.data.customToken);

                          return next.handle(this.addAuthenticationToken(request)).pipe(
                              map((event: HttpEvent < any > ) => {
                                  if (event instanceof HttpResponse) {
                                      if (event.body && event.body.responseData) {
                                          if (this.checkIsEncrption(request.url) === false) {
                                              event = event.clone < any > ({
                                                  body: this.encryption.decrypt(
                                                      event.body.responseData
                                                  ),
                                              });
                                          }
                                      }
                                  }
                                  return event;
                              })
                          );
                      }),
                      catchError((err: any) => {
                          this.refreshTokenInProgress = false;
                          if (err.error && err.error.statusCode === 400) {
                              return this.handleError(err, this.notification);
                          } else {
                            this.authService.onNotify(true); // For Drawer Close
                            this.authService.logout();
                            return this.handleError(err, this.notification);
                          }
                      })
                  );
              }
          })
      );
  }

  private handleError(error: any, notification: NzNotificationService) {
      if (error.error && error.error instanceof ErrorEvent) {
          // A client-side or network error occurred. Handle it accordingly.
          notification.error('error', 'An error occurred:' + error.error.message, {
              nzKey: 'key',
          });
      } else if (error instanceof SessionExpiredError) {
          notification.error('error', error.message, {
              nzKey: 'key'
          });
          this.router.navigate([AuthRoutes.login]);
      } else if (error instanceof HttpErrorResponse) {
        if (error.url.includes(urls.AuthUrls.tokenValidate)) {
        } else {
          this.handleHttpErrorResponse(error, notification);
        }
      }
      return throwError(error);
  }

  private checkIsEncrption(url) {
      if (url.includes('/attachment/create/') || url.includes('/download/') || url.includes('/upload') || 
      url.includes('merchant/image/upload')) {
          console.log('checkIsEncrption');
          return true;
      } else {
          return false;
      }
  }

  private handleHttpErrorResponse(
      error: HttpErrorResponse,
      notification: NzNotificationService
  ) {
      switch (error.status) {
          case 401:
              notification.error(error.error.message, '', {
                  nzKey: 'key'
              });
              break;
          case 400:
              if (error.error.message === Messages.noData) {
                  break;
              } else {
                  notification.error(error.error.message, '', {
                      nzKey: 'key'
                  });
                  break;
              }
              case 404:
                notification.error(error.error.message, '', {
                    nzKey: 'key'
                });
                break;
              default:
                  if (error.status === 0) {
                    notification.error(Messages.unKnownError, '', {
                        nzKey : 'key'
                    });
                  } else {
                    notification.error('error', 'An error occurred:' + error.message, {
                        nzKey: 'key',
                    });
                  }
      }
  }

  private addAuthenticationToken(request) {
      if (request.url.includes(urls.AuthUrls.login)) {
          return request;
      }

      const authToken: string = this.authService.getAuthToken();
      let newHeaders = request.headers;
      if (authToken) {
          // If we have a token, we append it to our new headers
          newHeaders = newHeaders.append('Authorization', `${authToken}`);
          newHeaders = newHeaders.append('X-API-KEY', Config.apiKey);
      }
      // Finally we have to clone our request with our new headers
      // This is required because HttpRequests are immutable
      return request.clone({
          headers: newHeaders
      });
  }
  closeDrawer(value) {
      if (value) {
          return value;
      }
  }
}
